import { c as _c } from "react/compiler-runtime";
import { useIntl } from '@onefootprint/hooks';
import type { EntityOnboarding } from '@onefootprint/request-types/dashboard';
import { Button } from '@onefootprint/ui';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import StatusBadge from '@/components/status-badge';
import useOnboardingKindText from '@/hooks/use-onboarding-kind-text';
import { hasVisibleStatus, onboardingHasPlaybook } from '../../../onboardings-shared/utils';
import useOnboardingTitle from '../../hooks/use-onboarding-title';
import useOpenTrace from '../../hooks/use-open-trace';
import DecryptAllButton, { type DecryptAction } from '../decrypt-all-button';
import OnboardingDetails from './components/onboarding-details';
type OnboardingHeaderProps = {
  onboarding: EntityOnboarding;
  /** Omitted once nothing the onboarding collected is left encrypted, and 'pending' until that's known. */
  decrypt?: DecryptAction | 'pending';
};
const OnboardingHeader = t0 => {
  const $ = _c(51);
  const {
    onboarding,
    decrypt
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "onboardings"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const {
    formatDateWithTime
  } = useIntl();
  const onboardingKindT = useOnboardingKindText();
  const getTitle = useOnboardingTitle();
  const openTrace = useOpenTrace();
  const playbook = onboardingHasPlaybook(onboarding) ? onboarding.playbook : undefined;
  let t2;
  if ($[1] !== formatDateWithTime) {
    t2 = timestamp => formatDateWithTime(new Date(timestamp), {
      month: "short",
      year: "numeric"
    });
    $[1] = formatDateWithTime;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const formatTimestamp = t2;
  const titleRef = useRef(null);
  let t3;
  if ($[3] !== getTitle || $[4] !== onboarding) {
    t3 = getTitle(onboarding);
    $[3] = getTitle;
    $[4] = onboarding;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== playbook || $[7] !== t) {
    t4 = playbook && <>{" "}<span className="text-tertiary">{playbook.isHidden ? t(_temp) : t(_temp2, {
          version: playbook.version
        })}</span></>;
    $[6] = playbook;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <h3 ref={titleRef} tabIndex={-1} className="min-w-0 text-heading-4 text-primary">{t3}{t4}</h3>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== onboarding) {
    t6 = hasVisibleStatus(onboarding) && <StatusBadge status={onboarding.status} />;
    $[12] = onboarding;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">{t5}{t6}</div>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== onboarding.kind || $[18] !== onboardingKindT || $[19] !== playbook) {
    t8 = playbook && `${onboardingKindT(onboarding.kind)} · `;
    $[17] = onboarding.kind;
    $[18] = onboardingKindT;
    $[19] = playbook;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== formatTimestamp || $[22] !== onboarding.timestamp) {
    t9 = formatTimestamp(onboarding.timestamp);
    $[21] = formatTimestamp;
    $[22] = onboarding.timestamp;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t8 || $[25] !== t9) {
    t10 = <p className="text-body-2 text-tertiary">{t8}{t9}</p>;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t7) {
    t11 = <div className="flex min-w-0 flex-col gap-1">{t7}{t10}</div>;
    $[27] = t10;
    $[28] = t7;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== decrypt || $[31] !== t) {
    t12 = decrypt === "pending" && <span className="invisible" aria-hidden={true}><Button size="compact">{t(_temp3)}</Button></span>;
    $[30] = decrypt;
    $[31] = t;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== decrypt) {
    t13 = decrypt && decrypt !== "pending" && <DecryptAllButton decrypt={decrypt} onRemovedWhileFocused={() => titleRef.current?.focus()} />;
    $[33] = decrypt;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== onboarding.hasGraphTrace || $[36] !== onboarding.id || $[37] !== openTrace || $[38] !== t) {
    t14 = onboarding.hasGraphTrace && <Button variant="secondary" size="compact" onClick={event => openTrace(event, onboarding.id)}>{t(_temp4)}</Button>;
    $[35] = onboarding.hasGraphTrace;
    $[36] = onboarding.id;
    $[37] = openTrace;
    $[38] = t;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== formatTimestamp || $[41] !== onboarding) {
    t15 = <OnboardingDetails onboarding={onboarding} formatTimestamp={formatTimestamp} />;
    $[40] = formatTimestamp;
    $[41] = onboarding;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t12 || $[44] !== t13 || $[45] !== t14 || $[46] !== t15) {
    t16 = <div className="flex min-w-0 flex-wrap items-center gap-2">{t12}{t13}{t14}{t15}</div>;
    $[43] = t12;
    $[44] = t13;
    $[45] = t14;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== t11 || $[49] !== t16) {
    t17 = <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 px-5 pt-5 pb-4">{t11}{t16}</header>;
    $[48] = t11;
    $[49] = t16;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  return t17;
};
export default OnboardingHeader;
function _temp($) {
  return $.header.unsaved;
}
function _temp2($_0) {
  return $_0.header.version;
}
function _temp3($_1) {
  return $_1.userData.decrypt.decryptAll;
}
function _temp4($_2) {
  return $_2.header.openTrace;
}